import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { enGB, nl } from 'date-fns/locale';
import {
  TbCalendarEvent,
  TbChecklist,
  TbEdit,
  TbExternalLink,
  TbForms,
  TbLink,
  TbMessageCircle,
  TbPhoto,
  TbPin,
  TbPinFilled,
  TbTrash,
  TbTrophy,
} from 'react-icons/tb';
import classNames from 'classnames';
import { Post, PostLinkedFeatureType } from '../../../types/post';
import Avatar from '../misc/Avatar';
import { ActionMenu, ActionMenuButton } from '../menus/ActionMenu';
import WarningModal from '../modals/WarningModal';
import postService from '../../../services/community/postService';
import useLanguage from '../../../hooks/account/useLanguage';
import useUserId from '../../../hooks/account/useUserId';
import Language from '../../../types/language';
// eslint-disable-next-line import/extensions, import/no-named-as-default
import PostEmojiPicker from './PostEmojiPicker';
// eslint-disable-next-line import/extensions
import PostReactionsModal from './PostReactionsModal';
// eslint-disable-next-line import/extensions
import PostCommentsSection from './PostCommentsSection';

const LINKED_ICONS: Record<PostLinkedFeatureType, React.ComponentType<{ className?: string }>> = {
  [PostLinkedFeatureType.EVENT]: TbCalendarEvent,
  [PostLinkedFeatureType.TASK]: TbChecklist,
  [PostLinkedFeatureType.SCORECARD]: TbTrophy,
  [PostLinkedFeatureType.PHOTO_ALBUM]: TbPhoto,
  [PostLinkedFeatureType.FORM]: TbForms,
};

interface PostWallCardProps {
  post: Post;
  onEdit: (post: Post) => void;
}

export default function PostWallCard({ post, onEdit }: PostWallCardProps): JSX.Element {
  const { t } = useTranslation();
  const lang = useLanguage();
  const currentUserId = useUserId();
  const locale = lang === Language.NL ? nl : enGB;
  const [expanded, setExpanded] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [reactionsModalOpen, setReactionsModalOpen] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(false);

  const authorName = `${post.author.firstName} ${post.author.lastName}`;
  const LinkedIcon = post.linkedFeatureType ? LINKED_ICONS[post.linkedFeatureType] : null;

  const reactionGroups = useMemo(
    () =>
      post.reactions.reduce<Record<string, number>>((acc, r) => {
        acc[r.emoji] = (acc[r.emoji] ?? 0) + 1;
        return acc;
      }, {}),
    [post.reactions],
  );

  const myReactedEmojis = useMemo(
    () =>
      new Set(
        currentUserId
          ? post.reactions.filter((r) => r.userId === currentUserId).map((r) => r.emoji)
          : [],
      ),
    [post.reactions, currentUserId],
  );

  const totalReactions = post.reactions.length;

  const timeAgo = formatDistanceToNow(new Date(post.createdAt), { addSuffix: true, locale });

  const handleReaction = (emoji: string): void => {
    if (!currentUserId) return;
    if (myReactedEmojis.has(emoji)) {
      postService.removeReaction(post.postId, emoji, currentUserId);
    } else {
      postService.addReaction(post.postId, emoji, currentUserId);
    }
  };

  const handlePin = () => {
    postService.pinPost(post.postId, !post.isPinned);
  };

  const handleDelete = () => {
    postService.deletePost(post.postId);
    setDeleteModalOpen(false);
  };

  const contentLong = (post.content?.length ?? 0) > 280;

  return (
    <article className="overflow-hidden rounded-xl border border-secondary-200 bg-secondary-50">
      {post.isPinned && (
        <div className="flex items-center gap-1.5 bg-primary px-4 py-1.5">
          <TbPin className="h-3.5 w-3.5 text-secondary-50" />
          <span className="text-xs font-medium text-secondary-50">
            {t('page.communicationOverview.posts.pinned')}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar alias={authorName} src={post.author.picture ?? undefined} className="h-10 w-10 shrink-0" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-primary-900">{authorName}</p>
              <p className="text-xs text-primary-600">{timeAgo}</p>
            </div>
          </div>
          <ActionMenu>
            <ActionMenuButton
              icon={TbEdit}
              text={t('page.communicationOverview.posts.table.edit')}
              onClick={() => onEdit(post)}
            />
            <ActionMenuButton
              icon={post.isPinned ? TbPinFilled : TbPin}
              text={
                post.isPinned
                  ? t('page.communicationOverview.posts.table.unpin')
                  : t('page.communicationOverview.posts.table.pin')
              }
              onClick={handlePin}
            />
            <ActionMenuButton
              className="text-danger"
              icon={TbTrash}
              text={t('page.communicationOverview.posts.table.delete')}
              onClick={() => setDeleteModalOpen(true)}
            />
          </ActionMenu>
        </div>

        {post.content && (
          <div>
            <p
              className={classNames('whitespace-pre-wrap text-sm leading-relaxed text-primary-900', {
                'line-clamp-4': !expanded && contentLong,
              })}>
              {post.content}
            </p>
            {contentLong && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-1 text-sm font-semibold text-primary-700 hover:underline">
                {expanded ? t('general.showLess') : t('general.showMore')}
              </button>
            )}
          </div>
        )}

        {post.images.length > 0 && (
          <div
            className={classNames('gap-2', {
              'flex overflow-x-auto pb-1': post.images.length > 1,
              'grid grid-cols-1': post.images.length === 1,
            })}>
            {post.images.map((img) => (
              <img
                key={img.id}
                src={img.url}
                alt=""
                className={classNames('rounded-xl object-cover', {
                  'h-48 w-full': post.images.length === 1,
                  'h-36 w-36 shrink-0': post.images.length > 1,
                })}
              />
            ))}
          </div>
        )}

        {post.linkedFeatureType && post.linkedFeatureData && LinkedIcon && (
          <div className="flex items-start gap-3 rounded-xl bg-secondary-200 p-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-secondary-50">
              <LinkedIcon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-primary-600">
                {t(`page.communicationOverview.posts.linkedTypes.${post.linkedFeatureType}`)}
              </p>
              <p className="truncate text-sm font-semibold text-primary-900">
                {post.linkedFeatureData.title}
              </p>
              {post.linkedFeatureData.subtitle && (
                <p className="truncate text-xs text-primary-600">{post.linkedFeatureData.subtitle}</p>
              )}
            </div>
            {post.linkedFeatureData.imageUrl && (
              <img
                src={post.linkedFeatureData.imageUrl}
                alt=""
                className="h-12 w-12 shrink-0 rounded-lg object-cover"
              />
            )}
          </div>
        )}

        {post.externalUrl && (
          <a
            href={post.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-xl bg-secondary-200 p-3 text-sm text-primary-800 hover:bg-primary-100">
            <TbLink className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{post.externalUrl}</span>
            <TbExternalLink className="h-4 w-4 shrink-0 text-primary-600" />
          </a>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-secondary-200 pt-3">
          {Object.entries(reactionGroups).map(([emoji, count]) => (
            <button
              key={emoji}
              type="button"
              disabled={!currentUserId}
              onClick={() => handleReaction(emoji)}
              className={classNames(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm transition-colors disabled:cursor-default',
                myReactedEmojis.has(emoji)
                  ? 'border-primary bg-primary text-secondary-50'
                  : 'border-secondary-200 bg-secondary-100 hover:bg-secondary-200',
              )}>
              <span>{emoji}</span>
              <span
                className={classNames(
                  'text-xs font-medium',
                  myReactedEmojis.has(emoji) ? 'text-secondary-50' : 'text-primary-800',
                )}>
                {count}
              </span>
            </button>
          ))}
          {currentUserId && (
            <button
              type="button"
              onClick={() => setEmojiPickerOpen(true)}
              className="inline-flex items-center rounded-full border border-secondary-200 bg-secondary-100 px-2 py-0.5 text-sm hover:bg-secondary-200"
              aria-label={t('page.communicationOverview.posts.addReaction')}>
              😀
            </button>
          )}
          {totalReactions > 0 && (
            <button
              type="button"
              onClick={() => setReactionsModalOpen(true)}
              className="text-xs font-medium text-primary-700 hover:underline">
              {t('page.communicationOverview.posts.viewReactions', { count: totalReactions })}
            </button>
          )}
          <button
            type="button"
            onClick={() => setCommentsExpanded((v) => !v)}
            className={classNames(
              'ml-auto inline-flex items-center gap-1 text-sm transition-colors hover:text-primary-800',
              commentsExpanded ? 'font-semibold text-primary-900' : 'text-primary-600',
            )}>
            <TbMessageCircle className="h-4 w-4" />
            {post.commentsCount}
          </button>
        </div>
      </div>

      <PostCommentsSection post={post} expanded={commentsExpanded} />

      <PostEmojiPicker
        open={emojiPickerOpen}
        setOpen={setEmojiPickerOpen}
        onSelectEmoji={handleReaction}
      />
      <PostReactionsModal open={reactionsModalOpen} setOpen={setReactionsModalOpen} post={post} />

      <WarningModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title={t('page.communicationOverview.posts.deleteModal.title')}
        description={t('page.communicationOverview.posts.deleteModal.description')}
        onContinue={handleDelete}
        saveClassName="bg-danger text-secondary-50"
        button={t('page.communicationOverview.posts.table.delete')}
      />
    </article>
  );
}
